<template>
  <v-container
    fluid
    class="stock-adjustment-report-page"
    :class="{ 'theme--dark': $vuetify.theme.dark }"
  >
    <!-- TOP HEADER -->
    <div
      class="ps-page-header d-flex align-center justify-space-between flex-wrap mb-4"
      style="gap: 16px;"
    >
      <div class="d-flex align-center" style="gap: 12px;">
        <span class="ap-card-accent" />
        <div>
          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
            <h1 class="ps-page-title mb-0">
              {{ $t("report_stock_adjustment") }}
            </h1>
            <v-chip x-small color="primary" outlined class="font-weight-bold">
              {{ pagination.total }} {{ $t("items") || "items" }}
            </v-chip>
          </div>
          <div class="ps-page-subtitle">
            {{ $t("stock_adjustment_subtitle") }}
          </div>
        </div>
      </div>

      <div class="d-flex align-center flex-wrap" style="gap: 10px;">
        <v-btn
          icon
          outlined
          small
          class="rounded-lg sync-btn"
          :loading="loadingTable"
          :title="$t('refresh') || 'Refresh'"
          @click="refreshAll"
        >
          <v-icon small color="#64748b">fal fa-sync-alt</v-icon>
        </v-btn>

        <!-- Link to Create Stock Adjustment -->
        <v-btn
          depressed
          color="primary"
          class="white--text font-weight-bold text-none rounded-lg px-4"
          to="/manage-stock-adjustment"
        >
          <v-icon left small>fal fa-plus-circle</v-icon>
          {{ $t("new_adjustment_request") }}
        </v-btn>
      </div>
    </div>

    <!-- PRODUCT / INGREDIENT SCOPE TABS -->
    <div v-if="canViewIngredients" class="scope-toggle-wrap mb-3">
      <div class="br-tabs">
        <div
          class="br-tab"
          :class="{ 'br-tab--active': activeTab === 'product' }"
          @click="onTabChange('product')"
        >
          <v-icon
            left
            small
            class="mr-1"
            :color="activeTab === 'product' ? 'primary' : '#94a3b8'"
          >
            fal fa-box
          </v-icon>
          {{ $t("product_item") }}
        </div>
        <div
          class="br-tab"
          :class="{ 'br-tab--active': activeTab === 'ingredient' }"
          @click="onTabChange('ingredient')"
        >
          <v-icon
            left
            small
            class="mr-1"
            :color="activeTab === 'ingredient' ? 'primary' : '#94a3b8'"
          >
            fal fa-mortar-pestle
          </v-icon>
          {{ $t("ingredient_item") }}
        </div>
      </div>
    </div>

    <!-- METRICS KPI STRIP -->
    <KpiCards
      class="mb-4"
      :items="kpiItems"
      :loading="loadingTable && !adjustments.length"
      :skeleton="4"
    />

    <!-- MAIN REPORT CARD -->
    <v-card class="ap-card" outlined elevation="0">
      <!-- Toolbar: Status Filter Tabs + Search + Date Range -->
      <div class="toolbar-wrapper border-bottom px-4 py-3">
        <!-- Status Segmented Tabs (.br-tabs) -->
        <div class="d-flex align-center justify-space-between flex-wrap mb-3" style="gap: 12px;">
          <div class="br-tabs">
            <div
              v-for="st in statusTabList"
              :key="st.value"
              class="br-tab"
              :class="{ 'br-tab--active': statusFilter === st.value }"
              @click="onStatusChange(st.value)"
            >
              {{ st.text }}
              <span
                v-if="counts[st.value] !== undefined"
                class="br-tab-count"
                :class="{ 'br-tab-count--highlight': st.value === 'pending' && counts.pending > 0 }"
              >
                {{ counts[st.value] }}
              </span>
            </div>
          </div>

          <!-- Quick Date Presets -->
          <div class="date-presets d-flex align-center flex-wrap" style="gap: 6px;">
            <v-chip
              v-for="p in datePresets"
              :key="p.id"
              x-small
              outlined
              class="preset-chip"
              :class="{ 'preset-chip--active': activePreset === p.id }"
              @click="applyDatePreset(p)"
            >
              {{ p.label }}
            </v-chip>
          </div>
        </div>

        <!-- Filter Controls Row -->
        <v-row dense align="center" class="filter-controls-row">
          <!-- Search Field -->
          <v-col cols="12" md="4" lg="4">
            <TextSearch
              v-model="search"
              :placeholder="$t('search') + ' (' + $t('code') + ')...'"
              @search="onSearch"
              @onClear="onClearSearch"
            />
          </v-col>

          <!-- Start Date -->
          <v-col cols="12" sm="5" md="3" lg="3">
            <DatePicker
              v-model="start_date"
              :dense="true"
              :placeholder="false"
              label="start_date"
              :max="end_date"
              hide-details
              @input="onDateFilterChange"
            />
          </v-col>

          <!-- End Date -->
          <v-col cols="12" sm="5" md="3" lg="3">
            <DatePicker
              v-model="end_date"
              :dense="true"
              :placeholder="false"
              label="end_date"
              :min="start_date"
              hide-details
              @input="onDateFilterChange"
            />
          </v-col>

          <!-- Clear Filters -->
          <v-col cols="12" sm="2" md="2" lg="2" class="d-flex align-center justify-end">
            <v-btn
              text
              small
              color="error"
              class="text-none font-weight-bold"
              @click="clearFilters"
            >
              <v-icon left small>fal fa-redo</v-icon>
              {{ $t("clear_filter") }}
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- TABLE CONTAINER -->
      <div class="table-container">
        <v-data-table
          :headers="customHeaders"
          :items="adjustments"
          :loading="loadingTable"
          hide-default-footer
          class="adjustment-data-table elevation-0"
          :items-per-page="perPage"
        >
          <!-- Row index -->
          <template slot="item.index" slot-scope="{ index }">
            <span class="ps-row-index">
              {{ (pagination.current_page - 1) * perPage + index + 1 }}
            </span>
          </template>

          <!-- Code (clickable monospace badge) -->
          <template slot="item.code" slot-scope="{ item }">
            <div
              class="d-inline-flex align-center ps-code-badge"
              :title="$t('view_detail') || 'View Details'"
              @click="viewItem(item)"
            >
              <v-icon x-small class="mr-1" color="#5b39c9">fal fa-barcode</v-icon>
              <span>{{ item.code }}</span>
            </div>
          </template>

          <!-- Status badge -->
          <template slot="item.status" slot-scope="{ item }">
            <span class="stock-pill" :class="statusPillClass(item.status)">
              <v-icon x-small class="mr-1" :color="statusIconColor(item.status)">
                {{ statusIcon(item.status) }}
              </v-icon>
              {{ $t(item.status) }}
            </span>
          </template>

          <!-- Reason -->
          <template slot="item.reason" slot-scope="{ item }">
            <div class="ps-reason-text text-truncate" :title="item.reason">
              {{ item.reason || "-" }}
            </div>
          </template>

          <!-- Items count -->
          <template slot="item.details_count" slot-scope="{ item }">
            <v-chip x-small outlined color="primary" class="font-weight-bold">
              {{ item.details_count || 0 }} {{ $t("items") || "items" }}
            </v-chip>
          </template>

          <!-- Requested by -->
          <template slot="item.user" slot-scope="{ item }">
            <div class="d-flex flex-column min-w-0">
              <span class="font-weight-medium text-body-2 text-truncate">
                {{ userFullName(item.user) }}
              </span>
              <span class="text-caption grey--text">
                {{ formatDate(item.created_at) }}
              </span>
            </div>
          </template>

          <!-- Branch -->
          <template slot="item.branch" slot-scope="{ item }">
            <span class="text-body-2 text-truncate">
              {{ item.branch ? item.branch.name : "-" }}
            </span>
          </template>

          <!-- Approver -->
          <template slot="item.approver" slot-scope="{ item }">
            <div v-if="item.approver" class="d-flex flex-column min-w-0">
              <span class="font-weight-medium text-body-2 text-truncate">
                {{ userFullName(item.approver) }}
              </span>
              <span v-if="item.approved_at" class="text-caption grey--text">
                {{ formatDate(item.approved_at) }}
              </span>
            </div>
            <span v-else class="grey--text text-caption">-</span>
          </template>

          <!-- Date -->
          <template slot="item.date" slot-scope="{ item }">
            <span class="text-body-2 text-no-wrap">
              {{ formatDate(item.date || item.created_at, false) }}
            </span>
          </template>

          <!-- Actions -->
          <template slot="item.actions" slot-scope="{ item }">
            <div class="d-flex align-center justify-end" style="gap: 6px;">
              <v-btn
                v-if="item.status === 'pending' && canApprove"
                small
                depressed
                color="primary"
                class="rounded-lg text-none px-3 font-weight-bold"
                height="30"
                @click="viewItem(item)"
              >
                <v-icon left x-small>fal fa-clipboard-check</v-icon>
                {{ $t("review_and_approve") }}
              </v-btn>
              <button
                v-else
                class="ps-act"
                :title="$t('view_detail') || 'View Details'"
                @click="viewItem(item)"
              >
                <v-icon small>fal fa-eye</v-icon>
              </button>
            </div>
          </template>

          <!-- Empty State -->
          <template slot="no-data">
            <div class="ps-empty py-12 text-center">
              <div class="ps-empty__icon-wrap mb-3">
                <v-icon size="44" color="#94a3b8">fal fa-clipboard-check</v-icon>
              </div>
              <div class="ps-empty__title mb-1 font-weight-bold">
                {{ $t("no_stock_adjustments_found") }}
              </div>
              <div class="ps-empty__desc mb-4 grey--text text-caption">
                {{ $t("stock_adjustment_subtitle") }}
              </div>
              <v-btn
                depressed
                color="primary"
                class="rounded-lg text-none px-4 font-weight-bold"
                to="/manage-stock-adjustment"
              >
                <v-icon left small>fal fa-plus-circle</v-icon>
                {{ $t("new_adjustment_request") }}
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- PAGINATION BAR -->
      <div
        v-if="pagination.total > 0"
        class="pagination-bar px-4 py-3 border-top d-flex align-center justify-space-between flex-wrap"
        style="gap: 12px;"
      >
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="text-caption grey--text">
            {{ $t("showing") || "Showing" }}
            {{ (pagination.current_page - 1) * perPage + 1 }} -
            {{ Math.min(pagination.current_page * perPage, pagination.total) }}
            {{ $t("of") || "of" }} {{ pagination.total }}
          </span>
          <v-select
            v-model="perPage"
            :items="[15, 30, 50, 100]"
            dense
            outlined
            hide-details
            class="per-page-select ml-2"
            @change="fetchItem(1)"
          />
        </div>

        <v-pagination
          v-model="currentPage"
          :length="pagination.last_page"
          :total-visible="7"
          circle
          color="primary"
          @input="fetchItem(currentPage)"
        />
      </div>
    </v-card>

    <!-- DETAIL / APPROVAL MODAL -->
    <app-custom-dialog
      v-model="dialog"
      width="920px"
      :footer="false"
      :title="$t('adjustment_details')"
    >
      <div slot="activator" class="pa-2">
        <!-- Top Metadata Card -->
        <div class="dialog-meta-card pa-4 rounded-xl mb-4 border">
          <div class="d-flex align-center justify-space-between flex-wrap mb-3" style="gap: 10px;">
            <div class="d-flex align-center" style="gap: 10px;">
              <span class="ps-code-badge ps-code-badge--lg">
                <v-icon small class="mr-1" color="#5b39c9">fal fa-barcode</v-icon>
                {{ form.code }}
              </span>
              <span class="stock-pill" :class="statusPillClass(form.status)">
                <v-icon x-small class="mr-1" :color="statusIconColor(form.status)">
                  {{ statusIcon(form.status) }}
                </v-icon>
                {{ $t(form.status) }}
              </span>
            </div>

            <div class="text-caption grey--text">
              <v-icon x-small class="mr-1">fal fa-calendar</v-icon>
              {{ formatDate(form.date || form.created_at) }}
            </div>
          </div>

          <v-row dense class="meta-info-grid">
            <v-col cols="12" sm="6" md="3">
              <div class="meta-label">{{ $t("requested_by") }}</div>
              <div class="meta-value">{{ userFullName(form.user || form.requested_by) }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="meta-label">{{ $t("branch") }}</div>
              <div class="meta-value">{{ form.branch ? form.branch.name : "-" }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="meta-label">{{ $t("items") }}</div>
              <div class="meta-value font-weight-bold">
                {{ form.details ? form.details.length : 0 }} {{ $t("items") }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="meta-label">{{ $t("reason") }}</div>
              <div class="meta-value text-truncate" :title="form.reason">
                {{ form.reason || "-" }}
              </div>
            </v-col>
          </v-row>

          <!-- Approver / Reject Reason info -->
          <div
            v-if="form.approved_by_user || form.approver"
            class="approver-box mt-3 pt-3 border-top d-flex align-center justify-space-between flex-wrap"
            style="gap: 8px;"
          >
            <div class="d-flex align-center">
              <v-icon small color="success" class="mr-2">fal fa-check-circle</v-icon>
              <span class="text-caption">
                {{ form.status === "rejected" ? $t("rejected_by") : $t("approved_by") }}:
                <strong>{{ userFullName(form.approved_by_user || form.approver) }}</strong>
                <span v-if="form.approved_at" class="grey--text ml-1">
                  ({{ formatDate(form.approved_at) }})
                </span>
              </span>
            </div>
          </div>

          <div v-if="form.reject_reason" class="reject-reason-box mt-3 pa-3 rounded-lg">
            <div class="d-flex align-center error--text text-caption font-weight-bold mb-1">
              <v-icon x-small color="error" class="mr-1">fal fa-exclamation-circle</v-icon>
              {{ $t("reject_reason") }}:
            </div>
            <div class="text-body-2 font-weight-medium">{{ form.reject_reason }}</div>
          </div>
        </div>

        <!-- ITEMS BREAKDOWN TABLE -->
        <div class="items-table-card rounded-xl border overflow-hidden mb-4">
          <div class="py-2 px-3 border-bottom d-flex align-center justify-space-between bg-light">
            <div class="font-weight-bold text-caption">
              {{ $t("adjusted_items") }} ({{ form.details ? form.details.length : 0 }})
            </div>
            <div v-if="totalNetAdjustmentQty !== null" class="text-caption">
              {{ $t("net_adjustment_qty") }}:
              <strong :class="totalNetAdjustmentQty >= 0 ? 'success--text' : 'error--text'">
                {{ totalNetAdjustmentQty >= 0 ? '+' : '' }}{{ totalNetAdjustmentQty }}
              </strong>
            </div>
          </div>

          <v-simple-table dense class="dialog-items-table">
            <thead>
              <tr>
                <th style="width: 40px;">#</th>
                <th>{{ isIngredient ? $t("ingredient") : $t("product") }}</th>
                <th class="text-center">{{ $t("qty") }}</th>
                <th class="text-right">{{ $t("last_stock") }}</th>
                <th class="text-right">{{ $t("result_stock") }}</th>
                <th>{{ $t("note") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, idx) in form.details" :key="line.id || idx">
                <td class="text-caption grey--text">{{ idx + 1 }}</td>
                <td>
                  <div class="d-flex align-center py-2">
                    <div class="item-thumb-box mr-3">
                      <app-img
                        v-if="lineItemImg(line)"
                        :img="lineItemImg(line)"
                        width="36"
                        height="36"
                      />
                      <v-icon v-else size="18" color="#64748b">
                        {{ isIngredient ? "fal fa-mortar-pestle" : "fal fa-box" }}
                      </v-icon>
                    </div>
                    <div class="min-w-0">
                      <div class="font-weight-bold text-body-2 text-truncate">
                        {{ lineItemName(line) }}
                      </div>
                      <div v-if="lineVariantLabel(line)" class="text-caption grey--text">
                        {{ lineVariantLabel(line) }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <span
                    class="stock-pill"
                    :class="line.qty >= 0 ? 'stock-pill-normal' : 'stock-pill-out'"
                  >
                    {{ line.qty > 0 ? "+" : "" }}{{ line.qty }} {{ lineUnit(line) }}
                  </span>
                </td>
                <td class="text-right text-caption font-weight-medium">
                  {{ line.last_stock }} {{ lineUnit(line) }}
                </td>
                <td class="text-right text-caption font-weight-bold">
                  {{ line.current_stock !== null ? line.current_stock + ' ' + lineUnit(line) : "-" }}
                </td>
                <td class="text-caption grey--text">
                  {{ line.note || "-" }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>

        <!-- REVIEW & APPROVAL ACTIONS -->
        <template v-if="form.status === 'pending'">
          <div class="approval-panel pa-4 rounded-xl border bg-light">
            <div class="d-flex align-center mb-3">
              <v-icon color="primary" class="mr-2">fal fa-shield-check</v-icon>
              <div>
                <div class="font-weight-bold text-body-2">
                  {{ $t("review_and_approve") }}
                </div>
                <div class="text-caption grey--text">
                  {{ $t("confirm_approval") || "Please review the inventory counts before taking action." }}
                </div>
              </div>
            </div>

            <template v-if="canApprove">
              <v-textarea
                v-model="reject_reason"
                class="mb-3"
                outlined
                dense
                rows="2"
                hide-details
                :placeholder="$t('reject_reason') + ' (' + ($t('required_for_reject') || 'Required for reject') + ')...'"
              />

              <div class="d-flex align-center justify-end" style="gap: 12px;">
                <v-btn
                  color="error"
                  depressed
                  outlined
                  class="rounded-lg text-none px-4 font-weight-bold"
                  :loading="actionLoading"
                  :disabled="actionLoading"
                  @click="stockAdjustmentAction(form.id, 'rejected')"
                >
                  <v-icon left small>fal fa-times</v-icon>
                  {{ $t("reject") }}
                </v-btn>

                <v-btn
                  color="primary"
                  depressed
                  class="rounded-lg text-none px-5 font-weight-bold white--text"
                  :loading="actionLoading"
                  :disabled="actionLoading"
                  @click="stockAdjustmentAction(form.id, 'approved')"
                >
                  <v-icon left small>fal fa-check</v-icon>
                  {{ $t("approved") }}
                </v-btn>
              </div>
            </template>

            <div v-else class="text-caption grey--text font-weight-medium">
              <v-icon x-small color="warning" class="mr-1">fal fa-lock</v-icon>
              {{
                isIngredient
                  ? $t("permission_not_foundapprove_ingredient_adjustment")
                  : $t("no_permission_to_approve_stock_adjustment")
              }}
            </div>
          </div>
        </template>
      </div>
    </app-custom-dialog>

    <app-loading :overlay="overlay" />
  </v-container>
</template>

<script>
import DatePicker from "@/components/global/DatePicker";
import TextSearch from "@/components/global/TextSearch";
import { success, confirm, error } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import moment from "moment";

const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);

const tomorrow = new Date(
  Date.now() - new Date().getTimezoneOffset() * 60000 + 86400000
)
  .toISOString()
  .substr(0, 10);

export default {
  name: "ReportStockAdjustmentPage",
  mixins: [myMixin],
  components: {
    DatePicker,
    TextSearch,
  },
  data: () => ({
    dialog: false,
    overlay: false,
    loadingTable: true,
    actionLoading: false,
    activeTab: "product",
    statusFilter: "all",
    search: "",
    reject_reason: "",
    start_date: today,
    end_date: tomorrow,
    activePreset: "today",
    currentPage: 1,
    perPage: 30,
    adjustments: [],
    pagination: {
      total: 0,
      current_page: 1,
      per_page: 30,
      last_page: 1,
    },
    counts: {
      all: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
    },
    form: {},
    datePresets: [
      { id: "today", label: "Today", days: 0 },
      { id: "yesterday", label: "Yesterday", days: -1 },
      { id: "7days", label: "Last 7 Days", days: -7 },
      { id: "30days", label: "This Month", days: -30 },
    ],
  }),
  computed: {
    statusTabList() {
      return [
        { text: this.$t("all") || "All", value: "all" },
        { text: this.$t("pending") || "Pending", value: "pending" },
        { text: this.$t("approved") || "Approved", value: "approved" },
        { text: this.$t("rejected") || "Rejected", value: "rejected" },
      ];
    },
    customHeaders() {
      return [
        { text: "#", value: "index", width: "55px", sortable: false },
        { text: this.$t("code") || "Code", value: "code", sortable: false },
        { text: this.$t("status") || "Status", value: "status", sortable: false },
        { text: this.$t("reason") || "Reason", value: "reason", sortable: false },
        { text: this.$t("items") || "Items", value: "details_count", align: "center", sortable: false },
        { text: this.$t("requested_by") || "Requested By", value: "user", sortable: false },
        { text: this.$t("branch") || "Branch", value: "branch", sortable: false },
        { text: this.$t("approved_by") || "Decision By", value: "approver", sortable: false },
        { text: this.$t("date") || "Date", value: "date", align: "right", sortable: false },
        { text: this.$t("actions") || "Actions", value: "actions", align: "right", width: "120px", sortable: false },
      ];
    },
    isIngredient() {
      return this.activeTab === "ingredient";
    },
    canViewIngredients() {
      return (
        this.MyRole("create_ingredient_adjustment") ||
        this.MyRole("approve_ingredient_adjustment")
      );
    },
    endpoint() {
      return this.isIngredient ? "ingredient_adjustment" : "stock_adjustment";
    },
    canApprove() {
      return this.MyRole(
        this.isIngredient ? "approve_ingredient_adjustment" : "approve_stock_adjustment"
      );
    },
    kpiItems() {
      return [
        {
          key: "total",
          label: this.$t("total_adjustments") || this.$t("stock_adjustment") || "Total Requests",
          icon: "fal fa-clipboard-list",
          iconColor: "primary",
          tone: "primary",
          value: this.counts.all || this.pagination.total,
        },
        {
          key: "pending",
          label: this.$t("pending") || "Pending Review",
          icon: "fal fa-clock",
          iconColor: "warning",
          tone: "warning",
          value: this.counts.pending,
        },
        {
          key: "approved",
          label: this.$t("approved") || "Approved",
          icon: "fal fa-check-circle",
          iconColor: "success",
          tone: "success",
          value: this.counts.approved,
        },
        {
          key: "rejected",
          label: this.$t("rejected") || "Rejected",
          icon: "fal fa-ban",
          iconColor: "error",
          tone: "danger",
          value: this.counts.rejected,
        },
      ];
    },
    totalNetAdjustmentQty() {
      if (!this.form.details || !this.form.details.length) return null;
      return this.form.details.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
    },
  },
  created() {
    this.refreshAll();
  },
  methods: {
    async refreshAll() {
      await Promise.all([
        this.fetchItem(this.currentPage),
        this.fetchSummaryCounts(),
      ]);
    },
    async onTabChange(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.currentPage = 1;
      this.dialog = false;
      await this.refreshAll();
    },
    async onStatusChange(status) {
      if (this.statusFilter === status) return;
      this.statusFilter = status;
      this.currentPage = 1;
      await this.fetchItem(1);
    },
    async onSearch() {
      this.currentPage = 1;
      await this.refreshAll();
    },
    async onClearSearch() {
      this.search = "";
      this.currentPage = 1;
      await this.refreshAll();
    },
    async onDateFilterChange() {
      this.activePreset = null;
      this.currentPage = 1;
      await this.refreshAll();
    },
    applyDatePreset(preset) {
      this.activePreset = preset.id;
      const now = moment();
      if (preset.id === "today") {
        this.start_date = now.format("YYYY-MM-DD");
        this.end_date = now.clone().add(1, "day").format("YYYY-MM-DD");
      } else if (preset.id === "yesterday") {
        this.start_date = now.clone().subtract(1, "day").format("YYYY-MM-DD");
        this.end_date = now.format("YYYY-MM-DD");
      } else if (preset.id === "7days") {
        this.start_date = now.clone().subtract(7, "days").format("YYYY-MM-DD");
        this.end_date = now.clone().add(1, "day").format("YYYY-MM-DD");
      } else if (preset.id === "30days") {
        this.start_date = now.clone().startOf("month").format("YYYY-MM-DD");
        this.end_date = now.clone().endOf("month").format("YYYY-MM-DD");
      }
      this.currentPage = 1;
      this.refreshAll();
    },
    async clearFilters() {
      this.search = "";
      this.statusFilter = "all";
      this.start_date = today;
      this.end_date = tomorrow;
      this.activePreset = "today";
      this.currentPage = 1;
      await this.refreshAll();
    },
    async fetchItem(page = 1) {
      this.currentPage = page;
      this.loadingTable = true;
      try {
        const params = new URLSearchParams();
        if (this.statusFilter && this.statusFilter !== "all") {
          params.append("status", this.statusFilter);
        } else {
          params.append("status", "all");
        }
        if (this.search) {
          params.append("q", this.search.trim());
        } else {
          if (this.start_date) params.append("start_date", this.start_date);
          if (this.end_date) params.append("end_date", this.end_date);
        }
        params.append("page", String(this.currentPage));
        params.append("per_page", String(this.perPage));

        const { data } = await this.$http.get(`${this.endpoint}?${params.toString()}`);
        if (data.status) {
          const resp = data.data;
          if (Array.isArray(resp)) {
            this.adjustments = resp;
            this.pagination = {
              total: resp.length,
              current_page: 1,
              per_page: this.perPage,
              last_page: 1,
            };
          } else if (resp && Array.isArray(resp.data)) {
            this.adjustments = resp.data;
            this.pagination = {
              total: resp.total || resp.data.length,
              current_page: resp.current_page || 1,
              per_page: resp.per_page || this.perPage,
              last_page: resp.last_page || 1,
            };
          } else {
            this.adjustments = [];
            this.pagination = { total: 0, current_page: 1, per_page: this.perPage, last_page: 1 };
          }
        }
      } catch (err) {
        console.error("fetchItem error:", err);
      } finally {
        this.loadingTable = false;
      }
    },
    async fetchSummaryCounts() {
      try {
        const baseParams = new URLSearchParams();
        if (this.search) {
          baseParams.append("q", this.search.trim());
        } else {
          if (this.start_date) baseParams.append("start_date", this.start_date);
          if (this.end_date) baseParams.append("end_date", this.end_date);
        }
        baseParams.append("per_page", "1");

        const [allRes, pendingRes, approvedRes, rejectedRes] = await Promise.allSettled([
          this.$http.get(`${this.endpoint}?${baseParams.toString()}&status=all`),
          this.$http.get(`${this.endpoint}?${baseParams.toString()}&status=pending`),
          this.$http.get(`${this.endpoint}?${baseParams.toString()}&status=approved`),
          this.$http.get(`${this.endpoint}?${baseParams.toString()}&status=rejected`),
        ]);

        if (allRes.status === "fulfilled" && allRes.value.data?.status) {
          this.counts.all = allRes.value.data.data?.total ?? 0;
        }
        if (pendingRes.status === "fulfilled" && pendingRes.value.data?.status) {
          this.counts.pending = pendingRes.value.data.data?.total ?? 0;
        }
        if (approvedRes.status === "fulfilled" && approvedRes.value.data?.status) {
          this.counts.approved = approvedRes.value.data.data?.total ?? 0;
        }
        if (rejectedRes.status === "fulfilled" && rejectedRes.value.data?.status) {
          this.counts.rejected = rejectedRes.value.data.data?.total ?? 0;
        }
      } catch (err) {
        // Fallback to local counts
        this.counts.all = this.pagination.total;
        this.counts.pending = this.adjustments.filter((a) => a.status === "pending").length;
        this.counts.approved = this.adjustments.filter((a) => a.status === "approved").length;
        this.counts.rejected = this.adjustments.filter((a) => a.status === "rejected").length;
      }
    },
    async viewItem({ id }) {
      try {
        this.overlay = true;
        const { data } = await this.$http.get(`${this.endpoint}/${id}`);
        if (data.status) {
          this.form = data.data;
          this.reject_reason = "";
          this.dialog = true;
        }
      } catch (err) {
        error(this, "Failed to load adjustment details");
      } finally {
        this.overlay = false;
      }
    },
    lineItemName(line) {
      if (this.isIngredient) {
        return line.ingredient_name || line.ingredient?.name_la || line.ingredient?.name_en || "-";
      }
      return line.product_name || line.product?.name || "-";
    },
    lineVariantLabel(line) {
      return line.variant?.name || line.variant_label || "";
    },
    lineItemImg(line) {
      return line.img || line.product?.img || line.ingredient?.img || null;
    },
    lineUnit(line) {
      return line.unit_name || line.product?.unit?.name || line.ingredient?.unit?.name || "";
    },
    userFullName(user) {
      if (!user) return "-";
      return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username || "-";
    },
    formatDate(date, withTime = true) {
      if (!date) return "-";
      return moment(date).format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
    },
    statusPillClass(status) {
      const st = String(status || "").toLowerCase();
      if (st === "approved") return "stock-pill-normal";
      if (st === "pending") return "stock-pill-low";
      if (st === "rejected") return "stock-pill-out";
      return "stock-pill-slate";
    },
    statusIcon(status) {
      const st = String(status || "").toLowerCase();
      if (st === "approved") return "fal fa-check-circle";
      if (st === "pending") return "fal fa-clock";
      if (st === "rejected") return "fal fa-times-circle";
      return "fal fa-circle";
    },
    statusIconColor(status) {
      const st = String(status || "").toLowerCase();
      if (st === "approved") return "#16a34a";
      if (st === "pending") return "#d97706";
      if (st === "rejected") return "#ef4444";
      return "#64748b";
    },
    async stockAdjustmentAction(id, status) {
      if (status === "rejected" && !this.reject_reason) {
        error(this, "reject_reason_required");
        return;
      }

      const msg =
        status === "approved"
          ? "is_approved_stock_adjustment"
          : "is_reject_stock_adjustment";
      const { isConfirmed } = await confirm({
        props: this,
        text: msg,
      });
      if (!isConfirmed) return;

      const form = { status };
      if (status === "rejected") {
        form.reject_reason = this.reject_reason;
      }

      try {
        this.actionLoading = true;
        const { data } = await this.$http.post(`${this.endpoint}_action/${id}`, form);
        if (data.status) {
          this.dialog = false;
          await this.refreshAll();
          await success(this, data.message);
        }
      } catch (err) {
        error(this, "Error executing action");
      } finally {
        this.actionLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.stock-adjustment-report-page {
  width: 100%;
  padding-bottom: 32px;
}

/* Header */
.ps-page-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.ps-page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.sync-btn {
  border-color: #e2e8f0;
}

/* Accent Bar */
.ap-card-accent {
  display: inline-block;
  width: 4px;
  height: 28px;
  background-color: #5b39c9;
  border-radius: 4px;
}

/* Scope Toggle Wrap */
.scope-toggle-wrap .br-tabs {
  background: #f1f5f9;
}

/* Card */
.ap-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

.border-top {
  border-top: 1px solid #f1f5f9;
}

/* Row Index */
.ps-row-index {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
}

/* Monospace Code Badge */
.ps-code-badge {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #5b39c9;
  background: rgba(91, 57, 201, 0.08);
  padding: 3px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.ps-code-badge:hover {
  background: rgba(91, 57, 201, 0.15);
}

.ps-code-badge--lg {
  font-size: 14px;
  padding: 5px 12px;
  border-radius: 8px;
}

/* Stock Status Pill */
.stock-pill {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.stock-pill-out {
  background: #fef2f2;
  color: #ef4444;
}

.stock-pill-low {
  background: #fffbeb;
  color: #d97706;
}

.stock-pill-normal {
  background: #f0fdf4;
  color: #16a34a;
}

.stock-pill-slate {
  background: #f1f5f9;
  color: #64748b;
}

/* Reason Text */
.ps-reason-text {
  max-width: 220px;
  font-size: 13px;
  color: #334155;
}

/* Action button */
.ps-act {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.ps-act--danger:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fef2f2;
}

/* Preset Chips */
.preset-chip {
  cursor: pointer;
  border-color: #e2e8f0;
  transition: all 0.15s ease;
}

.preset-chip:hover {
  border-color: #5b39c9;
  color: #5b39c9;
}

.preset-chip--active {
  background-color: rgba(91, 57, 201, 0.08) !important;
  border-color: #5b39c9 !important;
  color: #5b39c9 !important;
  font-weight: 600;
}

/* Highlight badge for pending count */
.br-tab-count--highlight {
  background: #fef3c7 !important;
  color: #b45309 !important;
  font-weight: 700 !important;
}

/* Per page selector */
.per-page-select {
  width: 75px;
}

/* Dialog details styling */
.dialog-meta-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.meta-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 2px;
}

.meta-value {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.reject-reason-box {
  background: #fef2f2;
  border: 1px solid #fee2e2;
}

.items-table-card {
  border: 1px solid #e2e8f0;
}

.item-thumb-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.bg-light {
  background: #f8fafc;
}

.approval-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

/* Dark Theme Support */
.theme--dark .ps-page-title {
  color: #f1f5f9;
}

.theme--dark .ps-page-subtitle {
  color: #94a3b8;
}

.theme--dark .ap-card,
.theme--dark .dialog-meta-card,
.theme--dark .items-table-card,
.theme--dark .approval-panel {
  background: #1e1e2d;
  border-color: #2d2d3f;
}

.theme--dark .bg-light {
  background: #252538;
}

.theme--dark .meta-value {
  color: #f1f5f9;
}

.theme--dark .item-thumb-box {
  background: #28283a;
  border-color: #3f3f56;
}

.theme--dark .border-bottom,
.theme--dark .border-top {
  border-color: #2d2d3f;
}

.theme--dark .ps-act {
  background: #28283a;
  border-color: #3f3f56;
  color: #94a3b8;
}

.theme--dark .ps-act:hover {
  color: #a78bfa;
  border-color: #a78bfa;
  background: rgba(167, 139, 250, 0.1);
}

.theme--dark .stock-pill-out {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.theme--dark .stock-pill-low {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

.theme--dark .stock-pill-normal {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}
</style>
